/**
 * SuggestionsInboxPage — a single place to review pending suggestions
 * across every tree the user owns AND every canonical person they
 * originated. Groups by person so a stack of edits on one ancestor
 * reads as a single coherent review.
 *
 * Approving calls PATCH /suggestions/:id with action=approve — the API
 * writes the suggested_value onto the canonical person, stamps
 * last_edited_by, and returns the updated suggestion row.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../services/api.js'
import { useTreeStore } from '../stores/tree.js'
import { usePageTitle } from '../hooks/usePageTitle.js'
import PostBountyModal from '../components/mysteries/PostBountyModal.jsx'
import { purchasesAllowed } from '../utils/platform.js'

const FIELD_LABELS = {
  first_name:     'First name',
  preferred_name: 'Preferred name',
  last_name:      'Last name',
  birth_name:     'Birth name',
  gender:         'Gender',
  birth_date:     'Birth date',
  birth_place:    'Birth place',
  death_date:     'Death date',
  death_place:    'Death place',
  occupation:     'Occupation',
  notes:          'Notes',
}

export default function SuggestionsInboxPage() {
  usePageTitle('Suggestions')
  const navigate = useNavigate()
  const [suggestions, setSuggestions] = useState(null)
  const [busy, setBusy] = useState(null) // suggestion.id currently being reviewed
  const fetchTree = useTreeStore(st => st.fetchTree)

  async function load() {
    const list = await api.get('/suggestions/inbox').catch(() => [])
    setSuggestions(list || [])
  }
  useEffect(() => { load() }, [])

  async function review(s, action) {
    setBusy(s.id)
    try {
      await api.patch(`/suggestions/${s.id}`, { action })
      // Remove from local state so the badge count updates on next
      // sidebar poll without a full reload.
      setSuggestions(prev => prev.filter(x => x.id !== s.id))
      // Approving writes the value onto the (global) person — refresh that
      // tree's data so the change is live in the tree view without a manual
      // reload. No-op if the tree isn't currently loaded.
      if (action === 'approve' && s.tree_id) fetchTree(s.tree_id)
    } catch (err) {
      alert(err.message || 'Review failed')
    } finally {
      setBusy(null)
    }
  }

  if (suggestions === null) {
    return <div className="loading" style={{ padding: '3rem', textAlign: 'center' }}>Loading…</div>
  }

  // Group agent-origin suggestions by research_query_id so an Origin AI
  // run reads as a single case file. User-origin stays grouped by person
  // as before. Groups render in interleaved created_at order — whichever
  // group's newest item is newest wins first position.
  const grouped = {}
  for (const s of suggestions) {
    const key = s.origin === 'agent' && s.research_query_id
      ? `agent:${s.research_query_id}`
      : `person:${s.person_id || 'none'}`
    ;(grouped[key] = grouped[key] || []).push(s)
  }

  async function approveHighConfidence(items) {
    const targets = items.filter(s =>
      (s.confidence == null ? 0 : Number(s.confidence)) >= 0.8 &&
      s.kind === 'enrich_field'
    )
    for (const s of targets) {
      try {
        await api.patch(`/suggestions/${s.id}`, { action: 'approve' })
      } catch { /* keep going — partial success is fine */ }
    }
    await load()
  }

  return (
    <div style={{ padding: '2.5rem 3rem', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: '0.5rem' }}>
          Review queue
        </div>
        <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '2.1rem', margin: 0, color: 'var(--text)' }}>
          Suggestions
        </h1>
        <p style={{ color: 'var(--text-muted)', maxWidth: 640, marginTop: '0.5rem', lineHeight: 1.55 }}>
          Pending changes other researchers have proposed on persons you originated, or on trees you own.
          Approve applies the change to the canonical record — visible to every tree that has this person.
        </p>
      </div>

      {suggestions.length === 0 ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--paper)', borderRadius: 12, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: '1.2rem', color: 'var(--text)', marginBottom: '0.3rem' }}>
            Nothing to review right now.
          </div>
          <div style={{ fontSize: '0.9rem' }}>
            New suggestions will land here as other researchers contribute to persons you've added.
          </div>
          {/* Re-engagement loop: an empty inbox is the moment a user is most
              likely to drift. Point them at the marketplace so there's always
              a next thing to do — solve someone else's lead and earn cash. */}
          <div style={{ marginTop: '2rem', paddingTop: '1.75rem', borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: '1.3rem', marginBottom: '0.4rem' }}>🔎</div>
            <div style={{ fontFamily: 'var(--font-serif)', fontSize: '1.05rem', color: 'var(--text)', marginBottom: '0.3rem' }}>
              While you wait — crack someone else's mystery.
            </div>
            <div style={{ fontSize: '0.9rem', maxWidth: 440, margin: '0 auto 1.1rem', lineHeight: 1.55 }}>
              Other researchers have open leads you could solve. Earn XP, build
              your solver profile, and pick up cash bounties along the way.
            </div>
            <button className="btn btn-primary" onClick={() => navigate('/mysteries')}>
              Browse the Marketplace →
            </button>
          </div>
        </div>
      ) : (
        Object.entries(grouped).map(([groupKey, items]) => {
          const first = items[0]
          const isAgent = first.origin === 'agent'
          const personName = [first.person_first_name, first.person_last_name].filter(Boolean).join(' ') || 'Unknown'
          const headline = isAgent
            ? `Origin AI research — ${personName}`
            : personName
          const highConfCount = items.filter(s => Number(s.confidence || 0) >= 0.8 && s.kind === 'enrich_field').length

          return (
            <div key={groupKey} style={{
              background: '#fff', border: '1px solid var(--border)', borderRadius: 12,
              padding: '1.25rem 1.5rem', marginBottom: '1.25rem',
              borderLeft: isAgent ? '3px solid var(--gold)' : undefined,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.9rem', gap: '1rem' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <button
                    onClick={() => first.person_id && navigate(`/tree/${first.tree_id}/person/${first.person_id}`)}
                    style={{
                      background: 'none', border: 'none', padding: 0, cursor: first.person_id ? 'pointer' : 'default',
                      fontFamily: 'var(--font-serif)', fontSize: '1.15rem', fontWeight: 600,
                      color: 'var(--text)', textAlign: 'left',
                    }}
                  >
                    {headline}
                  </button>
                  {isAgent && first.research_summary && (
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '0.4rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                      {first.research_summary}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  {isAgent && <span className="badge badge-gold" style={{ marginBottom: '.25rem' }}>AI-proposed</span>}
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {items.length} {items.length === 1 ? 'proposal' : 'proposals'}
                    {first.tree_name ? ` · ${first.tree_name}` : ''}
                  </div>
                  {isAgent && highConfCount > 0 && (
                    <button
                      className="btn btn-primary btn-sm"
                      style={{ marginTop: '.5rem' }}
                      onClick={() => approveHighConfidence(items)}
                    >
                      Approve {highConfCount} high-confidence
                    </button>
                  )}
                </div>
              </div>

              {items.map(s => <SuggestionRow key={s.id} s={s} busy={busy} review={review} />)}
            </div>
          )
        })
      )}
    </div>
  )
}

function SuggestionRow({ s: initialS, busy, review }) {
  const [s, setS] = useState(initialS)
  const conf = s.confidence == null ? null : Number(s.confidence)
  const kind = s.kind || 'enrich_field'
  const QUEST_KINDS = ['investigate_gap', 'confirm_fact', 'find_photo']
  const canApplyOneClick = kind === 'enrich_field' || kind === 'external_finding' || QUEST_KINDS.includes(kind)
  // An owner-authored quest row (no from_mystery_id) gets the marketplace
  // control strip; a solver's answer (from_mystery_id set) is reviewed/approved.
  const isOwnerQuest = QUEST_KINDS.includes(kind) && !s.from_mystery_id
  const isSolverAnswer = QUEST_KINDS.includes(kind) && !!s.from_mystery_id
  // Marketplace state for this lead (only meaningful for owner quest rows)
  const promoted   = !!s.promoted_to_marketplace
  const hidden     = !!s.hidden_from_marketplace

  const [acting, setActing] = useState(false)
  const [showBountyModal, setShowBountyModal] = useState(false)

  async function setPromoted(v) {
    if (acting) return
    setActing(true)
    try {
      const r = await api.patch(`/mysteries/${s.id}`, { promoted: v })
      setS(prev => ({ ...prev, promoted_to_marketplace: r.promoted_to_marketplace }))
    } finally { setActing(false) }
  }
  async function setHidden(v) {
    if (acting) return
    if (v && !confirm('Hide this lead from the public marketplace and community board?')) return
    setActing(true)
    try {
      const r = await api.patch(`/mysteries/${s.id}`, { hidden: v })
      setS(prev => ({ ...prev, hidden_from_marketplace: r.hidden_from_marketplace }))
    } finally { setActing(false) }
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(140px, 1fr) minmax(0, 2fr) auto',
      gap: '1rem',
      padding: '0.75rem 0',
      borderTop: '1px solid var(--border)',
      alignItems: 'center',
    }}>
      <div>
        <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
          {kind === 'enrich_field' && (FIELD_LABELS[s.field] || s.field)}
          {kind === 'add_person' && 'Add person'}
          {kind === 'add_relationship' && 'New relationship'}
          {kind === 'claim_record' && 'Claim record'}
          {kind === 'investigate_gap' && 'Research lead'}
          {kind === 'confirm_fact' && (isSolverAnswer ? 'Fact verified' : 'Verify a fact')}
          {kind === 'find_photo' && (isSolverAnswer ? 'Photo found' : 'Find a photo')}
          {kind === 'external_finding' && `Found ${s.payload?.source_type || 'source'}`}
        </div>
        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
          {s.origin === 'agent' ? 'Origin AI' : s.suggested_by_name} · {new Date(s.created_at).toLocaleDateString()}
        </div>
        {conf != null && (
          <ConfidenceChip value={conf} />
        )}
      </div>
      <div>
        <ProposalBody s={s} />
        {s.note && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '.3rem' }}>"{s.note}"</div>}
        {Array.isArray(s.sources) && s.sources.length > 0 && (
          <div style={{ marginTop: '.3rem', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
            Sources: {s.sources.slice(0, 3).map((src, i) => (
              <span key={i} style={{ marginRight: '.4rem' }}>
                {src.url
                  ? <a href={src.url} target="_blank" rel="noopener noreferrer">{src.type || 'ref'}</a>
                  : (src.type || 'ref')}
                {src.id ? `:${String(src.id).slice(0, 8)}` : ''}
              </span>
            ))}{s.sources.length > 3 ? ` +${s.sources.length - 3}` : ''}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          {canApplyOneClick && (
            <button
              className="btn btn-primary btn-sm"
              disabled={busy === s.id}
              onClick={() => review(s, 'approve')}
            >
              {busy === s.id ? '…' : (isOwnerQuest ? 'Acknowledge' : 'Approve')}
            </button>
          )}
          <button
            className="btn btn-ghost btn-sm"
            disabled={busy === s.id}
            onClick={() => review(s, 'reject')}
            style={{ color: 'var(--text-muted)' }}
          >
            {canApplyOneClick ? 'Reject' : 'Dismiss'}
          </button>
        </div>
        {isOwnerQuest && (
          <div className="inbox-gap-controls">
            <div className="inbox-gap-controls__label">Marketplace</div>
            <button
              className={`inbox-gap-controls__btn ${promoted ? 'is-active' : ''}`}
              onClick={() => setPromoted(!promoted)}
              disabled={acting || hidden}
              title={hidden
                ? 'Unhide first to promote'
                : promoted
                  ? 'Currently promoted on the marketplace. Click to demote to the community board.'
                  : 'Highlight this lead on the marketplace (free, no bounty required).'}
            >
              {promoted ? '★ Promoted' : '★ Promote'}
            </button>
            {/* Bounty funding is a payment — hidden on native (Apple 3.1.1).
                Owners fund bounties on the web; Promote (free) stays. */}
            {purchasesAllowed() && (
              <button
                className="inbox-gap-controls__btn"
                onClick={() => setShowBountyModal(true)}
                disabled={acting || hidden}
                title={hidden ? 'Unhide first to add a bounty' : 'Attach a cash bounty to attract solvers'}
              >+ Bounty</button>
            )}
            <button
              className={`inbox-gap-controls__btn inbox-gap-controls__btn--danger ${hidden ? 'is-active' : ''}`}
              onClick={() => setHidden(!hidden)}
              disabled={acting}
              title={hidden
                ? 'Currently hidden from public surfaces. Click to make visible again.'
                : 'Hide this lead from both marketplace and community board.'}
            >
              {hidden ? 'Hidden' : 'Hide'}
            </button>
          </div>
        )}
      </div>
      {showBountyModal && (
        <PostBountyModal mysteryId={s.id} onClose={() => setShowBountyModal(false)} />
      )}
    </div>
  )
}

function ProposalBody({ s }) {
  const kind = s.kind || 'enrich_field'
  const p = s.payload || {}

  if (kind === 'enrich_field') {
    return (
      <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
        <s style={{ opacity: 0.7 }}>{s.current_value || <em>empty</em>}</s>
        {' → '}
        <strong style={{ color: 'var(--green)' }}>{s.suggested_value || <em>clear</em>}</strong>
      </div>
    )
  }
  if (kind === 'investigate_gap') {
    return (
      <div style={{ fontSize: '0.88rem' }}>
        <strong>{p.description}</strong>
        {(p.likely_era || p.likely_place) && (
          <span style={{ color: 'var(--text-muted)', marginLeft: '.4rem' }}>
            {[p.likely_era, p.likely_place].filter(Boolean).join(' · ')}
          </span>
        )}
      </div>
    )
  }
  if (kind === 'confirm_fact') {
    const isAnswer = !!s.from_mystery_id
    if (isAnswer) {
      return (
        <div style={{ fontSize: '0.88rem' }}>
          Verified <strong>{FIELD_LABELS[p.field] || p.field}</strong>
          {p.source_url && (
            <div style={{ marginTop: '.25rem' }}>
              <a href={p.source_url} target="_blank" rel="noopener noreferrer">{p.source_url}</a>
            </div>
          )}
          {p.source_note && (
            <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '.2rem' }}>
              "{p.source_note}"
            </div>
          )}
        </div>
      )
    }
    return (
      <div style={{ fontSize: '0.88rem' }}>
        <strong>{p.title || `Verify ${FIELD_LABELS[p.field] || p.field}`}</strong>
        <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '.2rem' }}>
          {FIELD_LABELS[p.field] || p.field}: <em>{p.current_value || 'empty'}</em>
        </div>
      </div>
    )
  }
  if (kind === 'find_photo') {
    const isAnswer = !!s.from_mystery_id
    return (
      <div style={{ fontSize: '0.88rem' }}>
        <strong>{isAnswer ? 'Photo uploaded for this person' : (p.title || 'Find a photo')}</strong>
        {p.description && !isAnswer && (
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '.2rem' }}>{p.description}</div>
        )}
        {p.note && isAnswer && (
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '.2rem' }}>"{p.note}"</div>
        )}
      </div>
    )
  }
  if (kind === 'add_person') {
    return (
      <div style={{ fontSize: '0.88rem' }}>
        <strong>{[p.first_name, p.last_name].filter(Boolean).join(' ')}</strong>
        {(p.birth_date || p.death_date) && (
          <span style={{ color: 'var(--text-muted)', marginLeft: '.4rem' }}>
            ({p.birth_date || '?'} – {p.death_date || '?'})
          </span>
        )}
      </div>
    )
  }
  if (kind === 'add_relationship') {
    return (
      <div style={{ fontSize: '0.88rem' }}>
        {p.type === 'partner' ? 'Partner' : 'Parent / child'}: {p.person_a?.slice(0, 8)} ↔ {p.person_b?.slice(0, 8)}
      </div>
    )
  }
  if (kind === 'claim_record') {
    return (
      <div style={{ fontSize: '0.88rem' }}>
        Link record {String(p.record_id || '').slice(0, 8)} → this person
      </div>
    )
  }
  if (kind === 'external_finding') {
    return (
      <div>
        <a
          href={p.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: '0.95rem',
            fontWeight: 600,
            color: 'var(--green, #3b6b5a)',
            textDecoration: 'none',
            display: 'inline-block',
            marginBottom: '.25rem',
          }}
        >
          {p.title || '(untitled)'} ↗
        </a>
        {(p.publication || p.published_date) && (
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '.3rem' }}>
            {[p.publication, p.published_date].filter(Boolean).join(' · ')}
          </div>
        )}
        {p.snippet && (
          <div style={{ fontSize: '0.85rem', color: 'var(--text)', fontStyle: 'italic', borderLeft: '2px solid var(--line)', paddingLeft: '.6rem' }}>
            "{p.snippet.length > 240 ? p.snippet.slice(0, 240) + '…' : p.snippet}"
          </div>
        )}
      </div>
    )
  }
  return null
}

function ConfidenceChip({ value }) {
  const pct = Math.round(value * 100)
  const tone = value >= 0.8 ? 'green' : value >= 0.6 ? 'amber' : 'grey'
  const palette = {
    green: { bg: 'rgba(50,120,80,.14)',  fg: 'var(--green)' },
    amber: { bg: 'rgba(200,150,40,.15)', fg: '#8a6a10' },
    grey:  { bg: 'rgba(120,120,120,.12)', fg: 'var(--text-muted)' },
  }[tone]
  return (
    <span style={{
      display: 'inline-block',
      marginTop: '.35rem',
      fontSize: '.7rem',
      padding: '.1rem .5rem',
      borderRadius: 999,
      background: palette.bg,
      color: palette.fg,
      fontWeight: 600,
    }}>
      {pct}% confident
    </span>
  )
}
